import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, useFormikContext } from 'formik';

import itemTypeService from '../../service/itemType.service';
import { FormErrorUI, FormLabelUI, InputUI, SelectUI } from '../Interface';
import { ITEM_TYPE_PROPERTY_DATA_TYPES } from '../ItemTypeProperty/ItemTypePropertyFields';

const mapTypeOptions = (rows = []) => {
  return rows.map((row) => ({
    label: row.name,
    value: row.id,
  }));
};

const getTypeLabelMap = () => {
  return ITEM_TYPE_PROPERTY_DATA_TYPES.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});
};

const arePropertyListsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;

  return left.every((item, index) => item?.id === right[index]?.id);
};

const areAssignmentsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;

  return left.every((item, index) => {
    const rightItem = right[index];

    return (
      item?.id === rightItem?.id &&
      item?.itemTypePropertyId === rightItem?.itemTypePropertyId &&
      item?.itemTypePropertyValueId === rightItem?.itemTypePropertyValueId &&
      item?.valueText === rightItem?.valueText &&
      item?.valueNumber === rightItem?.valueNumber &&
      item?.property?.id === rightItem?.property?.id
    );
  });
};

const getSelectedAssignments = (selectedValues) => {
  if (Array.isArray(selectedValues?.itemPropertyAssignment)) {
    return selectedValues.itemPropertyAssignment;
  }

  if (Array.isArray(selectedValues?.itemPropertyAssignments)) {
    return selectedValues.itemPropertyAssignments;
  }

  return [];
};

const createAssignmentRow = (property, existingAssignment) => {
  const comboValueId =
    existingAssignment?.itemTypePropertyValueId ??
    existingAssignment?.itemTypePropertyValue?.id ??
    '';

  return {
    id: existingAssignment?.id ?? null,
    itemTypePropertyId: property.id,
    itemTypePropertyValueId: comboValueId,
    valueText: existingAssignment?.valueText ?? '',
    valueNumber: existingAssignment?.valueNumber ?? '',
    property,
  };
};

const hasAssignmentValue = (assignment) => {
  const dataType = assignment?.property?.dataType;

  if (dataType === 'COMBO') {
    return Boolean(assignment?.itemTypePropertyValueId);
  }

  if (dataType === 'NUMBER' || dataType === 'DECIMAL') {
    return (
      assignment?.valueNumber !== '' &&
      assignment?.valueNumber !== null &&
      assignment?.valueNumber !== undefined
    );
  }

  return Boolean(assignment?.valueText?.trim());
};

const ItemPropertyAssignmentTab = ({ disabled, selectedValues }) => {
  const { values, errors, setFieldValue } = useFormikContext();
  const [itemTypeRows, setItemTypeRows] = useState([]);
  const [selectedTypeProperties, setSelectedTypeProperties] = useState([]);
  const detailCacheRef = useRef({});

  const typeLabelMap = useMemo(() => getTypeLabelMap(), []);
  const itemTypeOptions = useMemo(() => mapTypeOptions(itemTypeRows), [itemTypeRows]);
  const selectedAssignments = useMemo(() => getSelectedAssignments(selectedValues), [selectedValues]);

  useEffect(() => {
    itemTypeService.getData().then((data) => {
      const rows = Array.isArray(data) ? data : data?.rows ?? [];
      setItemTypeRows(rows);
    });
  }, []);

  useEffect(() => {
    const loadTypeDetails = async () => {
      if (!values.itemTypeId) {
        if (selectedTypeProperties.length > 0) {
          setSelectedTypeProperties([]);
        }
        if ((values.itemPropertyAssignment ?? []).length > 0) {
          setFieldValue('itemPropertyAssignment', [], false);
        }
        return;
      }

      let detail = detailCacheRef.current[values.itemTypeId];
      if (!detail) {
        detail = await itemTypeService.getItem(values.itemTypeId);
        detailCacheRef.current[values.itemTypeId] = detail;
      }

      const typeProperties = (detail?.itemTypePropertyLink ?? []).map((link) => link.itemTypeProperty).filter(Boolean);

      if (!arePropertyListsEqual(selectedTypeProperties, typeProperties)) {
        setSelectedTypeProperties(typeProperties);
      }

      const existingAssignments =
        Array.isArray(values.itemPropertyAssignment) && values.itemPropertyAssignment.length > 0
          ? values.itemPropertyAssignment
          : selectedAssignments;
      const assignmentByPropertyId = new Map(
        existingAssignments.map((assignment) => [assignment.itemTypePropertyId, assignment])
      );

      const nextAssignments = typeProperties.map((property) =>
        createAssignmentRow(property, assignmentByPropertyId.get(property.id))
      );

      if (!areAssignmentsEqual(values.itemPropertyAssignment ?? [], nextAssignments)) {
        setFieldValue('itemPropertyAssignment', nextAssignments, false);
      }
    };

    loadTypeDetails();
  }, [
    selectedAssignments,
    selectedTypeProperties,
    setFieldValue,
    values.itemPropertyAssignment,
    values.itemTypeId,
  ]);

  const handleTypeChange = async (e) => {
    const nextTypeId = e.target.value ? parseInt(e.target.value, 10) : null;
    await setFieldValue('itemPropertyAssignment', [], false);
    await setFieldValue('itemTypeId', nextTypeId, false);
  };

  const handleAssignmentChange = (propertyId, changes) => {
    const nextAssignments = (values.itemPropertyAssignment ?? []).map((assignment) =>
      assignment.itemTypePropertyId === propertyId ? { ...assignment, ...changes } : assignment
    );

    setFieldValue('itemPropertyAssignment', nextAssignments, false);
  };

  const renderValueField = (assignment) => {
    const { property } = assignment;
    const type = property?.dataType;

    if (type === 'COMBO') {
      const propertyOptions = property?.itemTypePropertyValue ?? [];
      const comboOptions = propertyOptions.map((option) => ({
        label: option.value,
        value: option.id,
      }));

      return (
        <SelectUI
          fullWidth
          value={assignment.itemTypePropertyValueId ?? ''}
          option={comboOptions}
          variant="standard"
          disabled={disabled}
          onClear={() => {
            handleAssignmentChange(property.id, {
              itemTypePropertyValueId: '',
            });
          }}
          onChange={(e) => {
            const nextValue = e.target.value ? parseInt(e.target.value, 10) : null;

            handleAssignmentChange(property.id, {
              itemTypePropertyValueId: nextValue,
            });
          }}
        />
      );
    }

    if (type === 'NUMBER' || type === 'DECIMAL') {
      return (
        <InputUI
          fullWidth
          type="number"
          value={assignment.valueNumber ?? ''}
          variant="standard"
          disabled={disabled}
          onChange={(e) => {
            handleAssignmentChange(property.id, {
              valueNumber: e.target.value,
            });
          }}
        />
      );
    }

    return (
      <InputUI
        fullWidth
        type="text"
        value={assignment.valueText ?? ''}
        variant="standard"
        disabled={disabled}
        onChange={(e) => {
          handleAssignmentChange(property.id, {
            valueText: e.target.value,
          });
        }}
      />
    );
  };

  return (
    <div className="mx-4 mb-5">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <div className="col-span-2 md:col-span-1">
          <Field
            name="itemTypeId"
            component={SelectUI}
            fullWidth
            label={<FormLabelUI text="Tulajdonság típus" />}
            variant="standard"
            helperText={<FormErrorUI message={undefined} />}
            onClear={() => {
              setFieldValue('itemPropertyAssignment', [], false);
              setFieldValue('itemTypeId', null, false);
            }}
            onChange={handleTypeChange}
            value={values.itemTypeId ?? ''}
            disabled={disabled}
            option={itemTypeOptions}
          />
        </div>
      </div>

      {selectedTypeProperties.length > 0 && (
        <div className="max-h-[400px] overflow-y-scroll overflow-x-hidden custom-scrollbar mt-5">
          <table className="border w-full">
            <thead className="bg-tableHeaderColor">
              <tr className="grid grid-cols-4">
                <th className="border p-3 col-span-2">Megnevezés</th>
                <th className="border p-3 col-span-2">Érték</th>
              </tr>
            </thead>
            <tbody>
              {(values.itemPropertyAssignment ?? []).map((assignment, index) => (
                <tr
                  key={assignment.itemTypePropertyId}
                  className={index % 2 === 0 ? 'bg-gray-100 grid grid-cols-4' : 'grid grid-cols-4'}
                >
                  <td className="border py-1 px-2 col-span-2">
                    <div className="font-medium">
                      {assignment.property?.name}
                      {assignment.property?.mandatory && <span className="text-red-600 ml-1">*</span>}
                    </div>
                  </td>
                  <td className="border py-1 px-2 col-span-2 relative">
                    <div className="relative w-full">{renderValueField(assignment)}</div>
                    {assignment.property?.mandatory && !hasAssignmentValue(assignment) && errors.itemPropertyAssignment && (
                      <div className="text-xs text-red-600 mt-1">Kötelező mező</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ItemPropertyAssignmentTab;
